import React, { createContext, useContext, useState } from 'react';

interface UploadFormContextType {
	activeStep: number;
	handleNext: () => void;
	handleBack: () => void;
	handleReset: () => void;
	setActiveStep: (step: number) => void;
	previousActiveStepRef: React.RefObject<number>;
	continueButtonRef: React.RefObject<HTMLButtonElement | null>;
	backButtonRef: React.RefObject<HTMLButtonElement | null>;
	resetButtonRef: React.RefObject<HTMLButtonElement | null>;
	stepperEnabled: boolean;
	enableStepper: (enable: boolean) => void;
}

const UploadFormContext = createContext<UploadFormContextType | undefined>(undefined);

export const UploadFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [activeStep, setActiveStep] = useState(0);
	const [stepperEnabled, enableStepper] = useState(false);

	const handleNext = () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleReset = () => {
		setActiveStep(0);
	};

	const previousActiveStepRef = React.useRef(activeStep);
	const continueButtonRef = React.useRef<HTMLButtonElement>(null);
	const backButtonRef = React.useRef<HTMLButtonElement>(null);
	const resetButtonRef = React.useRef<HTMLButtonElement>(null);

	const value = {
		activeStep,
		setActiveStep,
		handleNext,
		handleBack,
		handleReset,
		previousActiveStepRef,
		continueButtonRef,
		backButtonRef,
		resetButtonRef,
		stepperEnabled,
		enableStepper,
	};

	return <UploadFormContext.Provider value={value}>{children}</UploadFormContext.Provider>;
};

// Custom hook specifically for upload form
export const useUploadFormStepper = (): UploadFormContextType => {
	const ctx = useContext(UploadFormContext);
	if (!ctx) throw new Error('useUploadFormStepper must be used within UploadFormProvider');
	return ctx;
};
